// @ts-nocheck
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';

import {
  bootstrapUserPersona,
  fetchUserPersonaProfile,
  updateUserPersonaProfile,
} from '../util/api';
import {
  authenticateWithWeb3Signature,
  connectInjectedEvmWalletForAddress,
  fetchWeb3AuthNonce,
  signWeb3AuthMessage,
} from '../util/web3Auth';
import { useWalletSessionMaybe } from './WalletSessionContext';

export const PERSONA_TYPES = ['standard', 'merchant', 'runner'];
export const ACTIVE_PERSONA_STORAGE_KEY = 'bx_active_persona';
export const PENDING_INITIAL_PERSONA_KEY = 'bx_pending_initial_persona';

const defaultProfile = {
  initial_persona: 'standard',
  activated_personas: ['standard'],
  merchant_wallet_address: null,
  runner_wallet_address: null,
};

const ActivePersonaContext = createContext(null);

const readStoredPersona = () => {
  try {
    const value = sessionStorage.getItem(ACTIVE_PERSONA_STORAGE_KEY);
    return PERSONA_TYPES.includes(value) ? value : null;
  } catch {
    return null;
  }
};

const writeStoredPersona = persona => {
  try {
    sessionStorage.setItem(ACTIVE_PERSONA_STORAGE_KEY, persona);
  } catch {
    // ignore
  }
};

export const ActivePersonaProvider = ({ children }) => {
  const history = useHistory();
  const walletSession = useWalletSessionMaybe();
  const isSharetribeAuthenticated = useSelector(state => Boolean(state.auth?.isAuthenticated));
  const [profile, setProfile] = useState(null);
  const [activePersona, setActivePersonaState] = useState(readStoredPersona() || 'standard');
  const [loading, setLoading] = useState(true);
  const [switchModal, setSwitchModal] = useState(null);
  const [switchBusy, setSwitchBusy] = useState(false);
  const [switchError, setSwitchError] = useState(null);

  const isAuthenticated =
    isSharetribeAuthenticated || walletSession?.authMode === 'web3';

  const refreshProfile = useCallback(async () => {
    if (!isAuthenticated) {
      setProfile(null);
      setActivePersonaState('standard');
      setLoading(false);
      return null;
    }
    setLoading(true);
    try {
      const pending = sessionStorage.getItem(PENDING_INITIAL_PERSONA_KEY);
      if (pending && PERSONA_TYPES.includes(pending)) {
        sessionStorage.removeItem(PENDING_INITIAL_PERSONA_KEY);
        const boot = await bootstrapUserPersona(pending);
        setProfile(boot.profile);
        setActivePersonaState(pending);
        writeStoredPersona(pending);
        return boot.profile;
      }
      const data = await fetchUserPersonaProfile();
      setProfile(data.profile);
      const stored = readStoredPersona();
      const nextPersona =
        stored && data.profile?.activated_personas?.includes(stored)
          ? stored
          : data.profile?.initial_persona || 'standard';
      setActivePersonaState(nextPersona);
      writeStoredPersona(nextPersona);
      return data.profile;
    } catch {
      setProfile(defaultProfile);
      return defaultProfile;
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    refreshProfile();
  }, [refreshProfile]);

  const activatedPersonas = profile?.activated_personas || ['standard'];

  const getDelegatedWallet = useCallback(
    persona => {
      if (persona === 'merchant') {
        return profile?.merchant_wallet_address || null;
      }
      if (persona === 'runner') {
        return profile?.runner_wallet_address || null;
      }
      return null;
    },
    [profile]
  );

  const ensurePersonaWallet = useCallback(
    async targetPersona => {
      const delegated = getDelegatedWallet(targetPersona);
      if (!delegated) {
        return true;
      }
      const connected = walletSession?.walletAddress;
      if (
        walletSession?.authMode === 'web3' &&
        connected &&
        connected.toLowerCase() === delegated.toLowerCase()
      ) {
        return true;
      }
      setSwitchModal({ targetPersona, requiredAddress: delegated });
      return false;
    },
    [getDelegatedWallet, walletSession?.authMode, walletSession?.walletAddress]
  );

  const completePersonaSwitch = useCallback(
    targetPersona => {
      if (!activatedPersonas.includes(targetPersona)) {
        return false;
      }
      setActivePersonaState(targetPersona);
      writeStoredPersona(targetPersona);
      setSwitchModal(null);
      setSwitchError(null);
      if (targetPersona === 'merchant') {
        history.push('/merchant');
      } else if (targetPersona === 'runner') {
        history.push('/runner');
      } else {
        history.push('/');
      }
      return true;
    },
    [activatedPersonas, history]
  );

  const switchPersona = useCallback(
    async targetPersona => {
      if (!PERSONA_TYPES.includes(targetPersona)) {
        return false;
      }
      if (!activatedPersonas.includes(targetPersona)) {
        return false;
      }
      if (targetPersona === activePersona) {
        return true;
      }
      const walletOk = await ensurePersonaWallet(targetPersona);
      if (!walletOk) {
        return false;
      }
      return completePersonaSwitch(targetPersona);
    },
    [activatedPersonas, activePersona, completePersonaSwitch, ensurePersonaWallet]
  );

  const confirmPersonaWalletSwitch = useCallback(async () => {
    if (!switchModal?.requiredAddress) {
      return;
    }
    setSwitchBusy(true);
    setSwitchError(null);
    try {
      const { provider, walletAddress } = await connectInjectedEvmWalletForAddress(
        switchModal.requiredAddress
      );
      if (walletSession?.authMode !== 'web3') {
        const { nonce, message } = await fetchWeb3AuthNonce({ walletAddress, chain: 'evm' });
        const signature = await signWeb3AuthMessage({ provider, message });
        await authenticateWithWeb3Signature({ walletAddress, signature, nonce, chain: 'evm' });
        await walletSession?.refreshSession?.();
      }
      completePersonaSwitch(switchModal.targetPersona);
    } catch (error) {
      setSwitchError(error instanceof Error ? error.message : 'Wallet connection failed');
    } finally {
      setSwitchBusy(false);
    }
  }, [switchModal, walletSession, completePersonaSwitch]);

  const cancelPersonaWalletSwitch = useCallback(() => {
    setSwitchModal(null);
    setSwitchError(null);
  }, []);

  const savePersonaProfile = useCallback(async patch => {
    const data = await updateUserPersonaProfile(patch);
    setProfile(data.profile);
    return data.profile;
  }, []);

  const canAccessMerchant = activatedPersonas.includes('merchant') && activePersona === 'merchant';
  const canAccessRunner = activatedPersonas.includes('runner') && activePersona === 'runner';
  const showMerchantNav = activatedPersonas.includes('merchant') && activePersona !== 'runner';
  const showRunnerNav = activatedPersonas.includes('runner') && activePersona !== 'merchant';

  const value = useMemo(
    () => ({
      profile,
      activePersona,
      activatedPersonas,
      loading,
      isAuthenticated,
      canAccessMerchant,
      canAccessRunner,
      showMerchantNav,
      showRunnerNav,
      refreshProfile,
      switchPersona,
      savePersonaProfile,
      switchModal,
      switchBusy,
      switchError,
      confirmPersonaWalletSwitch,
      cancelPersonaWalletSwitch,
    }),
    [
      profile,
      activePersona,
      activatedPersonas,
      loading,
      isAuthenticated,
      canAccessMerchant,
      canAccessRunner,
      showMerchantNav,
      showRunnerNav,
      refreshProfile,
      switchPersona,
      savePersonaProfile,
      switchModal,
      switchBusy,
      switchError,
      confirmPersonaWalletSwitch,
      cancelPersonaWalletSwitch,
    ]
  );

  return (
    <ActivePersonaContext.Provider value={value}>{children}</ActivePersonaContext.Provider>
  );
};

export const useActivePersona = () => {
  const ctx = useContext(ActivePersonaContext);
  if (!ctx) {
    throw new Error('useActivePersona must be used within ActivePersonaProvider');
  }
  return ctx;
};

export const useActivePersonaMaybe = () => useContext(ActivePersonaContext);

export default ActivePersonaContext;
